import { ADMIN_LIMITS, userSchema, type CredentialField } from '@chatapp-inbox/shared';

// Validação local dos formulários: dá o aviso ANTES de enviar. O servidor repete tudo (e é quem
// decide); as regras e os tetos vêm de `ADMIN_LIMITS`, conferido contra a API real nos testes de
// contrato do servidor. Os textos são os mesmos que o servidor usa quando há equivalente.

/** A mesma regra de e-mail que o servidor aplica (`z.string().email()`). */
const emailFormat = userSchema.shape.email;

export const PASSWORD_RULE = `A senha deve ter entre ${ADMIN_LIMITS.passwordMin} e ${ADMIN_LIMITS.passwordMax} caracteres.`;

export type Errors<K extends string> = Partial<Record<K, string>>;

export function validateName(value: string): string | null {
  const name = value.trim();
  if (name === '') return 'Informe o nome.';
  if (name.length > ADMIN_LIMITS.userName) {
    return `O nome pode ter no máximo ${ADMIN_LIMITS.userName} caracteres.`;
  }
  return null;
}

function validateEmail(value: string): string | null {
  const email = value.trim();
  if (email === '') return 'Informe o e-mail.';
  if (email.length > ADMIN_LIMITS.userEmail)
    return `O e-mail pode ter no máximo ${ADMIN_LIMITS.userEmail} caracteres.`;
  if (!emailFormat.safeParse(email).success) {
    return 'Informe um e-mail válido, como nome@dominio.com.br.';
  }
  return null;
}

/** Senha de usuário desta caixa de entrada. A senha NÃO é aparada: espaços fazem parte dela. */
export function validatePassword(value: string): string | null {
  if (value === '') return `Informe a senha. ${PASSWORD_RULE}`;
  if (value.length < ADMIN_LIMITS.passwordMin || value.length > ADMIN_LIMITS.passwordMax) {
    return PASSWORD_RULE;
  }
  return null;
}

export type NewUserField = 'name' | 'email' | 'password';

export function validateNewUser(input: {
  name: string;
  email: string;
  password: string;
}): Errors<NewUserField> {
  const found: Errors<NewUserField> = {};
  const name = validateName(input.name);
  if (name) found.name = name;
  const email = validateEmail(input.email);
  if (email) found.email = email;
  const password = validatePassword(input.password);
  if (password) found.password = password;
  return found;
}

// ------------------------------------------------------------------ conexão do WhatsApp

/** Valores digitados no formulário da conexão, por chave de campo (`CredentialField.key`). */
export type ConnectionValues = Record<string, string>;

/**
 * Valida os campos que o provedor pede. Campo obrigatório vazio é recusado, MENOS o secreto que já
 * está guardado (`secretsSet`): em branco significa "manter o guardado". O tamanho máximo vem do
 * próprio campo. Texto comum é aparado antes de medir; o SEGREDO não: espaços fazem parte dele.
 */
export function validateConnection(
  fields: readonly CredentialField[],
  values: ConnectionValues,
  secretsSet: readonly string[],
): Errors<string> {
  const found: Errors<string> = {};
  for (const field of fields) {
    const raw = values[field.key] ?? '';
    const value = field.secret ? raw : raw.trim();
    const kept = field.secret && secretsSet.includes(field.key);
    if (value === '') {
      if (field.required && !kept) found[field.key] = `Preencha “${field.label}”.`;
    } else if (value.length > field.maxLength) {
      found[field.key] = `“${field.label}” pode ter no máximo ${field.maxLength} caracteres.`;
    }
  }
  return found;
}

/**
 * O que vai no corpo de `PUT /admin/whatsapp/connection`. Segredo em branco NÃO entra (o servidor
 * mantém o guardado; `""` no lugar dele poderia apagá-lo) e, quando entra, vai exatamente como
 * digitado. Texto comum vai sem os espaços das pontas, mesmo vazio (é como se limpa um campo).
 */
export function connectionPayload(
  fields: readonly CredentialField[],
  values: ConnectionValues,
): Record<string, string> {
  const payload: Record<string, string> = {};
  for (const field of fields) {
    const raw = values[field.key] ?? '';
    if (field.secret) {
      if (raw !== '') payload[field.key] = raw;
    } else {
      payload[field.key] = raw.trim();
    }
  }
  return payload;
}

// ------------------------------------------------------------------ demonstração

/**
 * Limites do corpo de `POST /admin/whatsapp/simulate/inbound` (os de `simulateInboundBodySchema`;
 * um teste os confere contra o schema, então um não muda sem o outro).
 */
export const SIMULATE_LIMITS = {
  phoneMin: 5,
  phoneMax: 30,
  nameMax: 120,
  textMax: 20_000,
} as const;

export type SimulateField = 'phone' | 'name' | 'text';

export function validateSimulation(input: {
  name: string;
  phone: string;
  text: string;
}): Errors<SimulateField> {
  const found: Errors<SimulateField> = {};
  const phone = input.phone.trim();
  if (phone === '') found.phone = 'Informe o telefone do cliente.';
  else if (phone.length < SIMULATE_LIMITS.phoneMin || phone.length > SIMULATE_LIMITS.phoneMax) {
    found.phone = `O telefone deve ter entre ${SIMULATE_LIMITS.phoneMin} e ${SIMULATE_LIMITS.phoneMax} caracteres.`;
  }
  if (input.name.trim().length > SIMULATE_LIMITS.nameMax) {
    found.name = `O nome pode ter no máximo ${SIMULATE_LIMITS.nameMax} caracteres.`;
  }
  const text = input.text.trim();
  if (text === '') found.text = 'Escreva a mensagem do cliente.';
  else if (text.length > SIMULATE_LIMITS.textMax) {
    found.text = `A mensagem pode ter no máximo ${SIMULATE_LIMITS.textMax.toLocaleString('pt-BR')} caracteres.`;
  }
  return found;
}
